import { DataGlueGraph, FunctionDefinition, NodeInstance, NodeInstanceDataPort } from '@glueapp/graph-types';
import { isFunctionReferenceValue } from '@glueapp/graph-types';

const matchFunctionPassThroughPort = new RegExp('.+:.+');

/**
 * Adds pass-through ports to node instances that have function-typed ports.
 *
 * Pass-through ports allow external values to be passed into functions that have
 * additional input ports beyond what the function type signature requires.
 *
 * For example, if a ForEachItemInList node has an "Item Processor" function port
 * that expects (Item, Index, List) inputs, but the actual function being referenced
 * has additional ports like "Threshold", this updater will add a "Item Processor:Threshold"
 * port to the ForEachItemInList node instance.
 *
 * This runs after type resolution but before validation to ensure:
 * - Types are fully resolved for proper port matching
 * - Pass-through ports are present for connection validation
 * - Port structure is stable (runs once, not repeatedly)
 */
export function addPassThroughPorts(graph: DataGlueGraph): DataGlueGraph {
    const updatedNodeInstances = graph.nodeInstances.map(nodeInstance => {
        return addPassThroughPortsToNodeInstance(nodeInstance, graph.functions || []);
    });

    // Also process function definitions' internal node instances
    const updatedFunctions = graph.functions?.map(func => ({
        ...func,
        nodeInstances: func.nodeInstances.map(nodeInstance => addPassThroughPortsToNodeInstance(nodeInstance, graph.functions || [])),
    }));

    return {
        ...graph,
        nodeInstances: updatedNodeInstances,
        functions: updatedFunctions,
    };
}

function addPassThroughPortsToNodeInstance(nodeInstance: NodeInstance, functions: FunctionDefinition[]): NodeInstance {
    // Find which ports are function-typed ports and returning early in case none exist
    const functionPortNames: Set<string> = new Set(
        nodeInstance.inPorts.filter(port => port.type.kind === 'function').map(port => port.name),
    );

    if (functionPortNames.size === 0) {
        return nodeInstance;
    }

    // Build new inPorts array with pass-through ports inserted after each function port
    const newInPorts: NodeInstanceDataPort[] = [];
    const existingPassThroughPortNames = new Set<string>();

    // First pass: collect existing pass-through port names
    for (const port of nodeInstance.inPorts) {
        if (port.name.includes(':')) {
            existingPassThroughPortNames.add(port.name);
        }
    }

    // Second pass: build new port list
    for (const port of nodeInstance.inPorts) {
        if (matchFunctionPassThroughPort.test(port.name)) {
            continue;
        }

        // Add the original port
        newInPorts.push(port);

        // Check if this is a function port
        if (functionPortNames.has(port.name) && port.type.kind === 'function') {
            const portValue = nodeInstance.portValues[port.name];

            // Check if the port has a function reference value
            if (isFunctionReferenceValue(portValue)) {
                const referencedFunc = functions.find(f => f.id === portValue.functionId);

                if (referencedFunc) {
                    const requiredInputNames = new Set(Object.keys(port.type.inputTypes));

                    // Find additional input ports in the function that aren't required
                    const additionalInputPorts = referencedFunc.inputPortDefinitions.filter(
                        portDef => !requiredInputNames.has(portDef.name),
                    );

                    // Add pass-through ports for each additional input
                    for (const portDef of additionalInputPorts) {
                        const passThroughPortName = `${port.name}:${portDef.name}`;

                        newInPorts.push({
                            name: passThroughPortName,
                            type: portDef.type,
                            optional: portDef.optional,
                        });
                    }
                }
            }
        }
    }

    return {
        ...nodeInstance,
        inPorts: newInPorts,
    };
}
